import { useState, useMemo, useCallback, type ElementType } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { NumberTicker } from '@/components/ui/NumberTicker';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ProductCard } from './ProductCard';
import { CatalogCard } from './CatalogCard';
import { DecorationMethodConflictModal } from './DecorationMethodConflictModal';
import { MissingPositionModal } from './MissingPositionModal';
import { ProductGridSkeleton } from './ProductCardSkeleton';
import { CategoryTabs, allCategoryEntry } from './CategoryTabs';
import { ProductFilterSidebar } from './ProductFilterSidebar';
import {
  blockingMismatch,
  filterCatalog,
  hasActiveCatalogFilters,
  mismatchLabel,
  positionLabel,
  requirementsFromPositions,
  toMatchProduct,
  type CatalogFilterState,
} from './catalogFilter';
import { matchProduct, type MismatchReason } from '@designdeck/shared';
import { useManufacturers } from '@/hooks/useProducts';
import { usePositionPreviews, useProducts, useCategories } from '@/hooks';
import { useAdaptedPreviews } from '@/hooks/useAdaptedPreviews';
import { ConfirmApplyModal } from '@/components/editor/ConfirmApplyModal';
import { useProductSelectionStore, useUIStore, useDesignStore } from '@/stores';
import type { ProductsSort } from '@/stores/uiStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { lowDpiWarningFor } from './adaptedDpi';
import { resolveEditPlacementDesignId } from './editPlacementTarget';
import { resolveProductClickAction } from './productClickAction';
import {
  wouldConflictWithActiveProducts,
  type DecorationCategory,
} from '@/lib/decoration-method';
import { transformProduct, type UIProduct } from '@/lib/product-transformer';
import { buildPrintContextParamsForPosition } from '@/lib/printContextBuilder';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  Filter,
  ChevronDown,
  Eye,
  Image as ImageIcon,
} from 'lucide-react';

type StatusFilter = 'all' | 'active' | 'inactive'; // aliased locally for readability

type ProductGridVariant = 'sidebar' | 'full';

interface ProductGridProps {
  variant?: ProductGridVariant;
  /** See ProductGridMode. */
  mode?: ProductGridMode;
}

/**
 * Product Grid with category tabs, design preview, and JUNIQE-style layout
 * Now connected to real API data
 */
/**
 * - `default`: the classic products step — every product, filters, prices.
 * - `catalog`: the guided entry's step 2 — browse the range on model shots or
 *   packshots with the design, and pick.
 * - `overview`: the guided entry's step 3 — only the picked products, with
 *   the same cards and prices as the classic step.
 */
export type ProductGridMode = 'default' | 'catalog' | 'overview';

export function ProductGrid({
  variant = 'sidebar',
  mode = 'default',
}: ProductGridProps) {
  const isFull = variant === 'full';
  const isCatalog = mode === 'catalog';
  const isOverview = mode === 'overview';
  const { t, i18n } = useTranslation();
  const Container: ElementType = isFull ? 'section' : 'aside';
  const containerClassName = cn(
    'flex flex-col overflow-hidden',
    isFull
      ? 'w-full bg-[var(--paper)] border border-[var(--line)] rounded-card'
      : 'flex-[4] bg-[var(--paper)] border-l border-[var(--line)]'
  );
  const gridWrapperClassName = cn(
    'flex-1 p-3 scrollbar-hide',
    isFull ? 'bg-[var(--paper)]' : 'overflow-y-auto bg-[var(--paper)]'
  );
  const gridClassName = cn(
    'grid',
    isFull
      ? cn(
          'dc-tile-grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6',
          // Overview tiles carry price inputs and need more width.
          isOverview && 'dc-tile-grid-roomy'
        )
      : // The classic editor's sidebar: three columns in the reference, sized
        // from the container under an embedding theme like every tile grid.
        'dc-tile-grid gap-3 grid-cols-3'
  );

  // Fetch products from API
  const { data: apiProducts, isLoading, error } = useProducts();

  // Fetch categories from API
  const { data: apiCategories } = useCategories();

  // Product selection store
  const {
    selections,
    focusedProductId,
    focusedColorId,
    toggleProduct,
    toggleColor,
    setFocusedProduct,
    setActivePosition,
  } = useProductSelectionStore();
  const statusFilter = useUIStore((state) => state.productsStatusFilter);
  const setStatusFilter = useUIStore((state) => state.setProductsStatusFilter);
  // Catalogue filters live in uiStore so they survive a trip to step 1.
  const activeCategory = useUIStore((state) => state.productsCategory);
  const setActiveCategory = useUIStore((state) => state.setProductsCategory);
  const manufacturerIds = useUIStore((state) => state.productsManufacturerIds);
  const toggleManufacturer = useUIStore(
    (state) => state.toggleProductsManufacturer
  );
  const onlyMatching = useUIStore((state) => state.productsOnlyMatching);
  const setOnlyMatching = useUIStore((state) => state.setProductsOnlyMatching);
  const sort = useUIStore((state) => state.productsSort);
  const setSort = useUIStore((state) => state.setProductsSort);
  const resetFilters = useUIStore((state) => state.resetProductFilters);
  const showDesignOnCatalog = useUIStore((state) => state.catalogShowDesign);
  const setShowDesignOnCatalog = useUIStore(
    (state) => state.setCatalogShowDesign
  );
  const { data: apiManufacturers } = useManufacturers();
  const masterPositions = useCanvasStateManager(
    (state) => state.masterPositions
  );

  // Get active position from store
  const activePosition = useProductSelectionStore(
    (state) => state.activePosition
  );

  const [showDecorationConflictModal, setShowDecorationConflictModal] =
    useState(false);
  const [missingPosition, setMissingPosition] = useState<{
    productName: string;
    positionLabel: string;
    reason: MismatchReason;
  } | null>(null);

  const navigate = useNavigate();
  const currentStep = useUIStore((state) => state.currentStep);
  const placementsByProduct = useCanvasStateManager(
    (state) => state.placementsByProduct
  );
  const productDesignCrops = useCanvasStateManager(
    (state) => state.productDesignCrops
  );
  const clearProductOverrides = useCanvasStateManager(
    (state) => state.clearProductOverrides
  );
  const designId = useDesignStore((state) => state.designId);
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const saveDesign = useDesignStore((state) => state.saveDesign);
  const [pendingSwitch, setPendingSwitch] = useState<{
    productId: string;
    colorId: string | null;
    name: string;
  } | null>(null);

  const hasOverrides = useCallback(
    (productId: string) =>
      Object.keys(placementsByProduct[productId] ?? {}).length > 0 ||
      Object.keys(productDesignCrops[productId] ?? {}).length > 0,
    [placementsByProduct, productDesignCrops]
  );

  // Get design previews for all positions
  const { previews: positionPreviews } = usePositionPreviews();

  // Derive stable primitive preview URLs for cards (prevents passing a new object to every card)
  const frontPreviewUrl = useMemo(
    () => positionPreviews?.front ?? null,
    [positionPreviews]
  );
  const backPreviewUrl = useMemo(
    () => positionPreviews?.back ?? positionPreviews?.['back-inside'] ?? null,
    [positionPreviews]
  );

  // Transform API products to UI products with selection state
  const products: UIProduct[] = useMemo(() => {
    if (!apiProducts) return [];

    return apiProducts.map((apiProduct) => {
      const selection = selections[apiProduct.id];
      const uiProduct = transformProduct(
        apiProduct,
        selection?.selectedColors || [],
        selection?.isActive || false
      );
      return {
        ...uiProduct,
        sellingPrice: selection?.sellingPrice ?? uiProduct.sellingPrice,
      };
    });
  }, [apiProducts, selections]);

  // Per-product placements (single-product edits) are shown on every step
  // with the same rule as the canvas; see useAdaptedPreviews.
  const { adaptations } = useAdaptedPreviews(products);

  // Get unique categories from products or API
  const categories = useMemo(() => {
    if (apiCategories && apiCategories.length > 0) {
      return [
        allCategoryEntry(t),
        ...apiCategories.map((c) => ({
          id: c.id,
          name: c.name,
          slug: c.slug,
        })),
      ];
    }

    // Fallback: extract from products
    const uniqueCategories = new Map<
      string,
      { id: string; name: string; slug: string }
    >();
    products.forEach((p) => {
      if (p.categoryId && p.category) {
        uniqueCategories.set(p.category, {
          id: p.categoryId,
          name: p.category,
          slug: p.category,
        });
      }
    });

    return [allCategoryEntry(t), ...Array.from(uniqueCategories.values())];
  }, [apiCategories, products, t]);

  // What the design needs per Position, from the stored master positions —
  // the same numbers the guided size readout is derived from.
  const requirements = useMemo(
    () => requirementsFromPositions(masterPositions),
    [masterPositions]
  );
  // The same run as a category, for the method each product card names.
  const designDecoration = useMemo<DecorationCategory | null>(() => {
    if (requirements.length === 0) return null;
    return requirements.some((r) => r.decoration === 'embroidery')
      ? 'embroidery'
      : 'print';
  }, [requirements]);

  const filterState: CatalogFilterState = useMemo(
    () => ({
      category: activeCategory,
      // The overview is the picked products by definition.
      status: isOverview ? 'active' : statusFilter,
      manufacturerIds: isOverview ? [] : manufacturerIds,
      onlyMatching: isOverview ? false : onlyMatching,
      sort,
      // Sort by the price the card shows: CatalogCard vs. ProductCard.
      priceKey: isCatalog ? 'startingCost' : 'blankCost',
    }),
    [
      activeCategory,
      statusFilter,
      manufacturerIds,
      onlyMatching,
      sort,
      isOverview,
      isCatalog,
    ]
  );
  const pickedCount = useMemo(
    () => products.filter((p) => p.isActive).length,
    [products]
  );
  const goToCatalog = useCallback(() => {
    useUIStore.getState().setCurrentStep('catalog');
    const params = new URLSearchParams(window.location.search);
    params.set('step', 'catalog');
    navigate({ search: params.toString() }, { replace: true });
  }, [navigate]);

  const catalog = useMemo(
    () => filterCatalog(products, filterState, requirements),
    [products, filterState, requirements]
  );
  const filteredProducts = useMemo(
    () => catalog.shown.map((entry) => entry.product),
    [catalog]
  );
  const mismatchByProduct = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const entry of catalog.shown) {
      map.set(entry.product.id, mismatchLabel(entry.match));
    }
    return map;
  }, [catalog]);

  // Sidebar data: per-category and per-manufacturer counts over the whole
  // catalogue, so a count never changes because of the filter it belongs to.
  // A category without products leads nowhere and is left out; "all" and the
  // active one always stay so the current filter never disappears.
  const sidebarCategories = useMemo(
    () =>
      categories
        .map((c) => ({
          ...c,
          count:
            c.slug === 'all'
              ? products.length
              : products.filter((p) => p.category === c.slug).length,
        }))
        .filter(
          (c) => c.slug === 'all' || c.slug === activeCategory || c.count > 0
        ),
    [categories, products, activeCategory]
  );
  // The tabs (compact variant, overview) never showed counts, and in the
  // overview a catalogue-wide count would not match the picked-only grid.
  const tabCategories = useMemo(
    () => sidebarCategories.map(({ count: _count, ...c }) => c),
    [sidebarCategories]
  );
  const sidebarManufacturers = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products) {
      if (p.manufacturerId) {
        counts.set(p.manufacturerId, (counts.get(p.manufacturerId) ?? 0) + 1);
      }
    }
    const named = new Map(
      (apiManufacturers ?? []).map((m) => [m.id, m.name] as const)
    );
    return [...counts.entries()]
      .map(([id, count]) => ({
        id,
        count,
        name:
          named.get(id) ??
          products.find((p) => p.manufacturerId === id)?.manufacturerName ??
          id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [products, apiManufacturers]);

  const matchHint = useMemo(() => {
    if (requirements.length === 0) return null;
    if (requirements.some((r) => r.decoration === 'embroidery')) {
      return t('creator.products.matchHintEmbroidery');
    }
    const parts = requirements.map((r) =>
      r.widthMm > 0
        ? t('creator.products.positionWidth', {
            position: positionLabel(r.slug),
            width: Math.round(r.widthMm / 10),
          })
        : positionLabel(r.slug)
    );
    // "A und B" / "A, B and C": the list joiner follows the language.
    const areas = new Intl.ListFormat(i18n.language, {
      style: 'long',
      type: 'conjunction',
    }).format(parts);
    return t('creator.products.matchHintPrint', { areas });
  }, [requirements, t, i18n.language]);

  /** "Front 23 cm" per used Position, from the adapted placement on this product. */
  const areaChipsFor = useCallback(
    (product: UIProduct): string[] => {
      const perPosition = adaptations?.get(product.id);
      if (!perPosition) return [];
      const chips: string[] = [];
      for (const requirement of requirements) {
        // An embroidered motif is stitched into the product's embroidery
        // field as is; a centimetre figure would suggest a print-style fit.
        if (requirement.decoration === 'embroidery') {
          chips.push(
            t('creator.products.embroideryChip', {
              position: positionLabel(requirement.slug),
            })
          );
          continue;
        }
        const adaptation = perPosition.get(requirement.slug);
        const maxWidthMm = product.matchPositions.find(
          (p) => p.slug === requirement.slug
        )?.maxWidthMm;
        if (!adaptation || !maxWidthMm) continue;
        const mm = adaptation.placement.relWidth * maxWidthMm;
        chips.push(
          t('creator.products.positionWidth', {
            position: positionLabel(requirement.slug),
            width: Math.round(mm / 10),
          })
        );
      }
      return chips;
    },
    [adaptations, requirements, t]
  );

  // Low print resolution after the carry-over: a percent box or a class
  // change can blow an image up well past the width it was made at.
  const dpiWarningFor = useCallback(
    (product: UIProduct): string | null => {
      const warning = lowDpiWarningFor({
        product,
        requirements,
        masterPositions,
        adaptations: adaptations?.get(product.id),
      });
      if (!warning) return null;
      return t('creator.products.lowDpi', {
        position: positionLabel(warning.slug),
        dpi: warning.dpi,
        minDpi: warning.minDpi,
      });
    },
    [adaptations, masterPositions, requirements, t]
  );

  // Handle product toggle
  const handleToggle = useCallback(
    (productId: string, isActive: boolean) => {
      // With a design in place the run decides the method, and the fit check
      // below reads every method a Position offers. The default-method
      // conflict gate would otherwise refuse a cap (embroidery by default)
      // next to a polo (embroidered on request) in one embroidery run. Without
      // a design it stays the only guard against mixing print and embroidery.
      if (
        isActive &&
        requirements.length === 0 &&
        wouldConflictWithActiveProducts(products, productId)
      ) {
        setShowDecorationConflictModal(true);
        return;
      }

      const product = products.find((p) => p.id === productId);

      // A product without a Position the design uses would drop that motif
      // silently on save. Say so instead of selecting it.
      if (isActive && product) {
        const blocking = blockingMismatch(
          matchProduct(toMatchProduct(product), requirements)
        );
        if (blocking) {
          setMissingPosition({
            productName: product.name,
            positionLabel: positionLabel(blocking.slug),
            reason: blocking.reason,
          });
          return;
        }
      }

      // Activating with an empty selection seeds the first color, so the
      // card always shows (and can publish) a real color.
      toggleProduct(productId, isActive, product?.colors[0]?.id);

      useDesignStore.getState().setHasUnsavedChanges(true);
    },
    [toggleProduct, products, requirements]
  );

  // Handle color toggle
  const handleColorToggle = useCallback(
    (productId: string, colorId: string) => {
      toggleColor(productId, colorId);
      useDesignStore.getState().setHasUnsavedChanges(true);
    },
    [toggleColor]
  );

  // Handle category change
  const handleCategoryChange = useCallback(
    (categorySlug: string) => {
      setActiveCategory(categorySlug);
    },
    [setActiveCategory]
  );

  // Execute the normal product switch (focus + active position update)
  const executeProductSwitch = useCallback(
    (productId: string, colorId: string | null) => {
      // Set focused product
      setFocusedProduct(productId, colorId);

      // Update activePositionId and activePrintArea for the new product
      const product = apiProducts?.find((p) => p.id === productId);
      if (product && product.positions && product.positions.length > 0) {
        // Find the position matching the current activePosition slug
        let matchingPosition = product.positions.find(
          (p) => p.slug === activePosition
        );

        if (!matchingPosition) {
          matchingPosition = product.positions[0];
        }
        if (!matchingPosition) return;

        if (!matchingPosition?.printArea) return;

        // Only a calibrated, configured template yields an area (R13); an
        // unconfigured position is not offered instead of a guessed area.
        const printContextParams = buildPrintContextParamsForPosition(
          product,
          matchingPosition.slug
        );
        if (!printContextParams) {
          console.error(
            `[ProductGrid] ${product.id}/${matchingPosition.slug}: no calibrated template`
          );
          return;
        }

        // Use the matched position's slug (not activePosition, in case of fallback)
        setActivePosition(
          matchingPosition.slug,
          matchingPosition.id,
          printContextParams
        );
      }
    },
    [setFocusedProduct, apiProducts, activePosition, setActivePosition]
  );

  // Handle product click for preview focus. A product that carries its own
  // placement must not silently lose it when the design step focuses it.
  const handleProductClick = useCallback(
    (productId: string, colorId: string | null) => {
      const action = resolveProductClickAction({
        isFocused: productId === focusedProductId,
        currentStep,
        hasOverrides: hasOverrides(productId),
      });
      if (action === 'none') return;

      // Block print + embroidery mix (same rule as toggle: only without a
      // design, where the run-aware fit check cannot decide yet)
      if (
        requirements.length === 0 &&
        wouldConflictWithActiveProducts(products, productId)
      ) {
        setShowDecorationConflictModal(true);
        return;
      }

      if (action === 'confirm') {
        const name =
          products.find((p) => p.id === productId)?.name ??
          t('creator.products.productFallback');
        setPendingSwitch({ productId, colorId, name });
        return;
      }

      executeProductSwitch(productId, colorId);
    },
    [
      focusedProductId,
      currentStep,
      hasOverrides,
      products,
      requirements,
      executeProductSwitch,
      t,
    ]
  );

  // Drop the product's own placement and focus it with the master design.
  const handleUseMasterPlacement = useCallback(() => {
    if (!pendingSwitch) return;
    clearProductOverrides(pendingSwitch.productId);
    useDesignStore.getState().setHasUnsavedChanges(true);
    executeProductSwitch(pendingSwitch.productId, pendingSwitch.colorId);
    setPendingSwitch(null);
  }, [pendingSwitch, clearProductOverrides, executeProductSwitch]);

  // Keep the product's own placement and open the single-product editor.
  const handleEditOnlyThisProduct = useCallback(async () => {
    if (!pendingSwitch) return;
    const id = await resolveEditPlacementDesignId({
      designId,
      hasUnsavedChanges,
      saveDesign,
    });
    if (!id) {
      toast.error(t('creator.toast.saveFailed'));
      return;
    }
    const colorParam = pendingSwitch.colorId
      ? `&color=${pendingSwitch.colorId}`
      : '';
    const targetProductId = pendingSwitch.productId;
    setPendingSwitch(null);
    navigate(`/creator/${id}?editProduct=${targetProductId}${colorParam}`);
  }, [pendingSwitch, designId, hasUnsavedChanges, saveDesign, navigate, t]);

  // Handle color click to update focused color (for preview)
  const handleColorClick = useCallback(
    (productId: string, colorId: string) => {
      // Only update focused color if this product is the focused one
      if (productId === focusedProductId) {
        setFocusedProduct(productId, colorId);
      }
    },
    [focusedProductId, setFocusedProduct]
  );

  // Loading state - use skeleton instead of spinner
  if (isLoading) {
    return (
      <Container className={containerClassName}>
        {/* Header Skeleton */}
        <div className="p-4 border-b border-[var(--line)]">
          <div className="h-5 w-32 bg-[var(--paper-3)] rounded animate-pulse mb-3" />
          <div className="flex items-center gap-3">
            <div className="h-4 w-12 bg-[var(--paper-3)] rounded animate-pulse" />
            <div className="flex gap-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="w-7 h-7 bg-[var(--paper-3)] rounded-full animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Category Tabs Skeleton */}
        <div className="px-4 py-2 border-b border-[var(--line)] flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-8 w-16 bg-[var(--paper-3)] rounded-control animate-pulse"
            />
          ))}
        </div>

        {/* Products Grid Skeleton */}
        <div className={gridWrapperClassName}>
          <ProductGridSkeleton count={6} />
        </div>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container
        className={cn(containerClassName, 'items-center justify-center p-4')}
      >
        <AlertCircle className="w-8 h-8 text-[var(--danger)]" />
        <p className="mt-2 text-sm text-[var(--danger)]">
          {t('creator.products.loadError')}
        </p>
        <p className="mt-1 text-xs text-[var(--mute)]">
          {error instanceof Error
            ? error.message
            : t('creator.products.unknownError')}
        </p>
      </Container>
    );
  }

  // Empty state
  if (!products.length) {
    return (
      <Container
        className={cn(containerClassName, 'items-center justify-center')}
      >
        <p className="text-sm text-[var(--mute)]">
          {t('creator.products.empty')}
        </p>
      </Container>
    );
  }

  return (
    <>
      <DecorationMethodConflictModal
        isOpen={showDecorationConflictModal}
        onClose={() => setShowDecorationConflictModal(false)}
      />
      <MissingPositionModal
        isOpen={missingPosition !== null}
        onClose={() => setMissingPosition(null)}
        productName={missingPosition?.productName ?? ''}
        positionLabel={missingPosition?.positionLabel ?? ''}
        reason={missingPosition?.reason}
      />
      <ConfirmApplyModal
        isOpen={pendingSwitch !== null}
        productName={pendingSwitch?.name ?? ''}
        title={t('creator.products.customPlacementTitle')}
        message={t('creator.products.customPlacementMessage', {
          name: pendingSwitch?.name ?? '',
        })}
        applyAllLabel={t('creator.products.useMasterPlacement')}
        applyProductLabel={t('creator.products.editOnlyProduct', {
          name: pendingSwitch?.name ?? '',
        })}
        onApplyAll={handleUseMasterPlacement}
        onApplyProduct={handleEditOnlyThisProduct}
        onCancel={() => setPendingSwitch(null)}
      />
      <Container className={containerClassName}>
        {isCatalog || isOverview ? (
          <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] p-4">
            <div className="flex items-baseline gap-3">
              <h3 className="dc-type-title text-[var(--ink)]">
                {isCatalog
                  ? t('creator.products.catalogTitle')
                  : t('creator.products.selectionTitle')}
              </h3>
              <span className="dc-type-label text-[var(--mute)]">
                {t('creator.products.pickedCount', { count: pickedCount })}
              </span>
            </div>
            {isCatalog && (
              /* The label says what the click will show, so the button needs
                 no active colour: an accent here competes with the product
                 tiles it is meant to describe. */
              <button
                type="button"
                onClick={() => setShowDesignOnCatalog(!showDesignOnCatalog)}
                className="flex h-[34px] items-center gap-2 rounded-control bg-[var(--paper)] px-3 text-[12.5px] font-semibold text-[var(--ink)] ring-1 ring-inset ring-[var(--line)] transition-colors hover:bg-[var(--paper-2)]"
              >
                {showDesignOnCatalog ? (
                  <ImageIcon className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {showDesignOnCatalog
                  ? t('creator.products.showCatalogImages')
                  : t('creator.products.showDesign')}
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 border-b border-[var(--line)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="dc-type-title text-[var(--ink)]">
                {t('creator.products.globalSettings')}
              </h3>

              {/* Status Filter Dropdown */}
              <div className="relative flex items-center gap-2">
                <Filter className="w-4 h-4 text-[var(--mute)]" />
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value as StatusFilter)
                    }
                    className={cn(
                      'text-sm font-medium rounded-control pl-3 pr-8 py-1.5 bg-[var(--paper)]',
                      'ring-1 ring-inset ring-[var(--line)]',
                      'focus:outline-none focus:ring-2 focus:ring-primary',
                      'cursor-pointer appearance-none',
                      statusFilter !== 'all' &&
                        'text-primary ring-primary-300 bg-primary-50'
                    )}
                  >
                    <option value="all">
                      {t('creator.products.status.all')}
                    </option>
                    <option value="active">
                      {t('creator.products.status.active')}
                    </option>
                    <option value="inactive">
                      {t('creator.products.status.inactive')}
                    </option>
                  </select>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none',
                      statusFilter !== 'all'
                        ? 'text-primary'
                        : 'text-[var(--mute)]'
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* The compact variant and the overview keep tabs; the full step and
            the catalogue get the rail. */}
        {(!isFull || isOverview) && (
          <CategoryTabs
            categories={tabCategories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        )}

        {/* The height chain the scrolling grid below hangs off. Without
            flex-1 and min-h-0 here the grid grows past the container, the
            aside clips it and the sidebar cannot be scrolled. */}
        <div
          className={cn(
            'flex min-h-0 flex-1',
            isFull ? 'items-stretch' : 'flex-col'
          )}
        >
          {isFull && !isOverview && (
            <ProductFilterSidebar
              categories={sidebarCategories}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              manufacturers={sidebarManufacturers}
              selectedManufacturerIds={manufacturerIds}
              onToggleManufacturer={toggleManufacturer}
              onlyMatching={onlyMatching}
              onToggleOnlyMatching={() => setOnlyMatching(!onlyMatching)}
              matchHint={matchHint}
              hasActiveFilters={hasActiveCatalogFilters(filterState)}
              onReset={resetFilters}
            />
          )}

          {/* Products Grid */}
          <div className={cn(gridWrapperClassName, isFull && 'min-w-0 flex-1')}>
            {isFull && (
              <div className="mb-3 flex items-center justify-between gap-3 px-1">
                <span className="dc-type-label text-[var(--mute)]">
                  <Trans
                    i18nKey="creator.products.shownOfTotal"
                    count={catalog.total}
                    components={{
                      shown: <NumberTicker value={filteredProducts.length} />,
                    }}
                  />
                  {catalog.hiddenByMatch > 0 && (
                    <>
                      {' · '}
                      {t('creator.products.hiddenByMatch', {
                        count: catalog.hiddenByMatch,
                      })}
                    </>
                  )}
                </span>
                <label className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                  {t('creator.products.sort')}
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as ProductsSort)}
                    className="cursor-pointer rounded-control bg-[var(--paper)] px-2 py-1 text-[12px] font-medium text-[var(--ink)] ring-1 ring-inset ring-[var(--line)] focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="default">
                      {t('creator.products.sortOptions.default')}
                    </option>
                    <option value="price-asc">
                      {t('creator.products.sortOptions.priceAsc')}
                    </option>
                    <option value="price-desc">
                      {t('creator.products.sortOptions.priceDesc')}
                    </option>
                    <option value="name">
                      {t('creator.products.sortOptions.name')}
                    </option>
                  </select>
                </label>
              </div>
            )}
            {/* Cards that leave a filter fade out and the rest close the
                gap. Deliberately no `layout` animation: measuring every card
                on each render made a big catalogue wobble while its images
                loaded, and each step change remounts the grid. */}
            <div className={gridClassName}>
              <AnimatePresence initial={false} mode="popLayout">
                {filteredProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    {isCatalog ? (
                      <CatalogCard
                        key={product.id}
                        product={product}
                        mismatchLabel={
                          mismatchByProduct.get(product.id) ?? null
                        }
                        areaChips={areaChipsFor(product)}
                        dpiWarning={dpiWarningFor(product)}
                        showDesign={showDesignOnCatalog}
                        frontPreviewUrl={frontPreviewUrl}
                        positionAdaptations={
                          adaptations?.get(product.id) ?? null
                        }
                        onToggle={handleToggle}
                      />
                    ) : (
                      <ProductCard
                        key={product.id}
                        product={product}
                        mismatchLabel={
                          mismatchByProduct.get(product.id) ?? null
                        }
                        areaChips={areaChipsFor(product)}
                        dpiWarning={dpiWarningFor(product)}
                        designDecoration={designDecoration}
                        frontPreviewUrl={frontPreviewUrl}
                        backPreviewUrl={backPreviewUrl}
                        positionAdaptations={
                          adaptations?.get(product.id) ?? null
                        }
                        mockupContainerClassName={
                          isFull ? 'aspect-[4/3]' : undefined
                        }
                        isCompactColors={!isFull}
                        isFocused={product.id === focusedProductId}
                        focusedColorId={
                          product.id === focusedProductId
                            ? focusedColorId
                            : null
                        }
                        onToggle={handleToggle}
                        onColorToggle={handleColorToggle}
                        onProductClick={handleProductClick}
                        onColorClick={handleColorClick}
                      />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {filteredProducts.length === 0 &&
              isOverview &&
              pickedCount === 0 && (
                <div className="m-1 flex flex-col items-start gap-2 rounded-card bg-[var(--paper-2)] p-5">
                  <span className="dc-type-label text-[var(--mute)]">
                    {t('creator.products.nothingPicked')}
                  </span>
                  <p className="text-[14px] leading-[1.5] text-[var(--ink-2)]">
                    {t('creator.products.nothingPickedHint')}
                  </p>
                  <button
                    type="button"
                    onClick={goToCatalog}
                    className="h-[34px] rounded-control bg-[var(--ink)] px-3.5 text-[13px] font-semibold text-[var(--paper)] hover:brightness-125"
                  >
                    {t('creator.products.toCatalog')}
                  </button>
                </div>
              )}

            {filteredProducts.length === 0 &&
              !(isOverview && pickedCount === 0) && (
                <div className="m-1 flex flex-col items-start gap-2 rounded-card bg-[var(--paper-2)] p-5">
                  <span className="dc-type-label text-[var(--mute)]">
                    {t('creator.products.noMatch')}
                  </span>
                  <p className="text-[14px] leading-[1.5] text-[var(--ink-2)]">
                    {onlyMatching && catalog.hiddenByMatch > 0
                      ? t('creator.products.noMatchMotif')
                      : t('creator.products.noMatchFilters')}
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="h-[34px] rounded-control bg-[var(--ink)] px-3.5 text-[13px] font-semibold text-[var(--paper)] hover:brightness-125"
                  >
                    {t('creator.filter.reset')}
                  </button>
                </div>
              )}
          </div>
        </div>
      </Container>
    </>
  );
}
